import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { Navigation, MapPin, Circle } from "lucide-react";

// Car SVG marker (top-down view) with a heading-rotated inner element.
// The outer wrapper stays axis-aligned so leaflet's zoom/pan transforms
// don't compound with the rotation; the inner <div> owns the rotate().
function buildCarIcon(bearingDeg = 0) {
  const CAR_SVG = `
    <svg viewBox="0 0 24 40" width="26" height="42" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#00E5FF"/>
          <stop offset="100%" stop-color="#00A0B4"/>
        </linearGradient>
      </defs>
      <rect x="4" y="6" width="16" height="28" rx="5" ry="5" fill="url(#g)" stroke="#050505" stroke-width="1.5"/>
      <rect x="6" y="10" width="12" height="7" rx="2" fill="#0A0A0A" opacity="0.85"/>
      <rect x="6" y="23" width="12" height="7" rx="2" fill="#0A0A0A" opacity="0.85"/>
      <circle cx="12" cy="4" r="1.8" fill="#FFC800"/>
      <rect x="3" y="12" width="1.5" height="14" fill="#050505"/>
      <rect x="19.5" y="12" width="1.5" height="14" fill="#050505"/>
    </svg>
  `;
  return L.divIcon({
    className: "car-icon-wrap",
    html: `
      <div class="car-icon-outer" style="width:26px;height:42px;transform:translate(-50%,-50%);position:relative;">
        <div class="car-icon-inner" style="transform:rotate(${bearingDeg}deg);transform-origin:50% 50%;transition:transform 1.6s cubic-bezier(.4,.2,.2,1);will-change:transform;filter:drop-shadow(0 4px 6px rgba(0,229,255,.35));">
          ${CAR_SVG}
        </div>
      </div>
    `,
    iconSize: [26, 42],
    iconAnchor: [13, 21],
  });
}

// Compute bearing (0..360) from point A to point B in degrees, north = 0.
function bearingBetween(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

const pickupIcon = L.divIcon({
  className: "pickup-icon-wrap",
  html: `
    <div style="width:22px;height:22px;transform:translate(-50%,-50%);border-radius:9999px;background:#ffffff;border:3px solid #0a0a0a;box-shadow:0 0 0 2px #ffffff;"></div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const dropoffIcon = L.divIcon({
  className: "dropoff-icon-wrap",
  html: `
    <div style="width:22px;height:22px;transform:translate(-50%,-50%);background:#000;border:3px solid #fff;box-shadow:0 0 0 2px #000;"></div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15, { animate: true });
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    }
  }, [map, JSON.stringify(points)]);
  return null;
}

/**
 * LiveTrackingMap
 * - Polls /api/orders/:orderId/driver-location every `pollMs` (default 4s)
 * - Shows driver dot, pickup pin, dropoff pin, and a straight-line distance.
 * - Height is controlled via className (parent) or `height` prop.
 */
export default function LiveTrackingMap({ orderId, pollMs = 4000, height = 260, className = "" }) {
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState(null);
  const [bearing, setBearing] = useState(0);
  const lastPos = useRef(null);
  const timer = useRef(null);

  const fetchOnce = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/driver-location`);
      // Compute bearing from previous → new driver position so the car icon
      // rotates to face the direction of travel.
      const loc = data?.driver?.location;
      if (loc?.lat != null && loc?.lng != null && lastPos.current) {
        const [pLat, pLng] = lastPos.current;
        // Only rotate on a meaningful move (>5m ≈ 0.00005 deg).
        if (Math.abs(loc.lat - pLat) > 0.00005 || Math.abs(loc.lng - pLng) > 0.00005) {
          setBearing(bearingBetween(pLat, pLng, loc.lat, loc.lng));
        }
      }
      if (loc?.lat != null && loc?.lng != null) lastPos.current = [loc.lat, loc.lng];
      setSnap(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to fetch driver location");
    }
  };

  useEffect(() => {
    fetchOnce();
    timer.current = setInterval(fetchOnce, pollMs);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, pollMs]);

  const pickup = snap?.pickup;
  const dropoff = snap?.dropoff;
  const driver = snap?.driver?.location;

  const pickupPt = pickup?.lat && pickup?.lng ? [pickup.lat, pickup.lng] : null;
  const dropoffPt = dropoff?.lat && dropoff?.lng ? [dropoff.lat, dropoff.lng] : null;
  const driverPt = driver?.lat && driver?.lng ? [driver.lat, driver.lng] : null;

  const focusPoints = useMemo(
    () => [driverPt, pickupPt, dropoffPt].filter(Boolean),
    [driverPt?.[0], driverPt?.[1], pickupPt?.[0], pickupPt?.[1], dropoffPt?.[0], dropoffPt?.[1]]
  );

  const centerFallback = pickupPt || driverPt || [40.7128, -74.006]; // NYC fallback

  // Distance driver -> pickup (straight line, km)
  const distanceKm = useMemo(() => {
    if (!driverPt || !pickupPt) return null;
    const [la1, ln1] = driverPt, [la2, ln2] = pickupPt;
    const R = 6371;
    const dLat = ((la2 - la1) * Math.PI) / 180;
    const dLng = ((ln2 - ln1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((la1 * Math.PI) / 180) * Math.cos((la2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }, [driverPt?.[0], driverPt?.[1], pickupPt?.[0], pickupPt?.[1]]);

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-ink-border bg-ink-surface2 ${className}`} data-testid="live-tracking-map" style={{ height }}>
      <MapContainer center={centerFallback} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pickupPt && (
          <Marker position={pickupPt} icon={pickupIcon}>
            <Popup>Pickup: {pickup?.address}</Popup>
          </Marker>
        )}
        {dropoffPt && (
          <Marker position={dropoffPt} icon={dropoffIcon}>
            <Popup>Dropoff: {dropoff?.address}</Popup>
          </Marker>
        )}
        {driverPt && (
          <Marker position={driverPt} icon={buildCarIcon(bearing)}>
            <Popup>{snap?.driver?.supplier_name || "Driver"}</Popup>
          </Marker>
        )}
        <FitBounds points={focusPoints} />
      </MapContainer>

      {/* Overlay status */}
      <div className="absolute top-2 left-2 right-2 flex items-center justify-between pointer-events-none">
        <div className="bg-black/70 backdrop-blur rounded-full px-3 py-1 flex items-center gap-1.5 text-[11px] font-black text-white" data-testid="tracking-status">
          <Circle size={8} fill="#00B14F" strokeWidth={0} className="animate-pulse" />
          {snap?.status ? snap.status.toUpperCase() : "…"}
        </div>
        {distanceKm !== null && (
          <div className="bg-white text-black rounded-full px-3 py-1 text-[11px] font-black flex items-center gap-1" data-testid="tracking-distance">
            <Navigation size={11} /> {distanceKm.toFixed(2)} km away
          </div>
        )}
      </div>

      {!driverPt && !err && (
        <div className="absolute bottom-2 left-2 right-2 bg-black/70 rounded-xl px-3 py-2 text-[11px] text-white/80 flex items-center gap-2" data-testid="tracking-waiting">
          <MapPin size={12} /> Waiting for driver to share live location…
        </div>
      )}
      {err && (
        <div className="absolute bottom-2 left-2 right-2 bg-black/70 rounded-xl px-3 py-2 text-[11px] text-brand-warn" data-testid="tracking-error">
          {err}
        </div>
      )}

      <style>{`
        @keyframes pulse { 0% { transform:scale(1); opacity:.4 } 70% { transform:scale(2.2); opacity:0 } 100% { opacity:0 } }
        .leaflet-container { background:#0a0a0a; }
        /* Smooth interpolated slide for the car marker between location updates */
        .car-icon-wrap { transition: transform 1.6s cubic-bezier(.4,.2,.2,1); will-change: transform; }
      `}</style>
    </div>
  );
}
